/**
 * PATH: packages/email-hub/src/feature/shortcuts/registry.ts
 * FILE_ID: EMAILHUB-SHORTCUTS-REGISTRY-001
 * PURPOSE: Canonical inbox shortcut table + pure key matcher — the single
 *   source both the keydown hook (useInboxHotkeys) and the display registry
 *   drift test consume, mirroring the Kanban registry pattern.
 * WHY: FEAT-2026-07-06-EMAIL-SHORTCUTS — operator: "implement the shortcut
 *   keys options for emails comprehensively just like Kanban". Landmine: `c`
 *   is COMPLETE (archive + flip linked card DONE + hide) per the same ask —
 *   "c should have been complete… not done d, leave d alone"; plain archive
 *   moved to `e`. `mod+x` is the ONLY modifier chord matched here — everything
 *   else must bail on mod keys so ⌘C/⌘A stay native.
 */

export type EmailShortcutScope = 'list' | 'reader'

export interface EmailShortcutDef {
  /** Stable identifier. */
  id: string
  /** Human label shown in the cheat sheet. */
  label: string
  /** Display keys (already human-formatted, mod rendered as ⌘/Ctrl). */
  displayKeys: string[]
  /** The exact `KeyboardEvent.key` the inbox handler matches (list scope). */
  matchKey?: string
  /** Chord requires ⌘ (mac) / Ctrl (win) held. */
  mod?: boolean
  /** Where the key is handled: the inbox list or the open-email reader. */
  scope: EmailShortcutScope
  /** Sub-grouping for the cheat sheet. */
  category: 'Navigation' | 'Triage' | 'Organise' | 'In the open email' | 'Help'
}

const MOD = '⌘/Ctrl'

/**
 * The comprehensive inbox set. List-scope rows are matched by
 * `matchEmailShortcut` inside useInboxHotkeys; reader-scope rows document the
 * handlers living in EmailPreviewPane / EmailReader / useGlobalMarkupShortcut
 * so the cheat sheet stays complete.
 */
export const EMAIL_SHORTCUTS: EmailShortcutDef[] = [
  // -- Navigation --------------------------------------------------------
  { id: 'move-down', label: 'Move to the next email', displayKeys: ['↓'], matchKey: 'ArrowDown', scope: 'list', category: 'Navigation' },
  { id: 'move-up', label: 'Move to the previous email', displayKeys: ['↑'], matchKey: 'ArrowUp', scope: 'list', category: 'Navigation' },
  { id: 'open', label: 'Open the selected email', displayKeys: ['Enter'], matchKey: 'Enter', scope: 'list', category: 'Navigation' },
  { id: 'deselect', label: 'Deselect the current email', displayKeys: ['Escape'], matchKey: 'Escape', scope: 'list', category: 'Navigation' },
  { id: 'compose', label: 'Compose a new email', displayKeys: ['n'], matchKey: 'n', scope: 'list', category: 'Navigation' },
  { id: 'focus-search', label: 'Focus the email search', displayKeys: ['/'], matchKey: '/', scope: 'list', category: 'Navigation' },
  // -- Triage ------------------------------------------------------------
  {
    id: 'complete',
    label: 'Complete — mark done (flips the linked card) and hide from view',
    displayKeys: ['c'],
    matchKey: 'c',
    scope: 'list',
    category: 'Triage',
  },
  { id: 'archive', label: 'Archive the hovered / selected email', displayKeys: ['e'], matchKey: 'e', scope: 'list', category: 'Triage' },
  { id: 'done', label: 'Done — archive the selected email(s)', displayKeys: ['d'], matchKey: 'd', scope: 'list', category: 'Triage' },
  { id: 'junk', label: 'Junk — move the selected email(s) to junk', displayKeys: ['j'], matchKey: 'j', scope: 'list', category: 'Triage' },
  { id: 'delete', label: 'Delete — move to the bin', displayKeys: [MOD, 'X'], matchKey: 'x', mod: true, scope: 'list', category: 'Triage' },
  { id: 'toggle-read', label: 'Mark read / unread', displayKeys: ['u'], matchKey: 'u', scope: 'list', category: 'Triage' },
  {
    id: 'quick-triage',
    label: 'Quick Triage — WHO / WHAT / SIGNALS / WHERE palette',
    displayKeys: ['t'],
    matchKey: 't',
    scope: 'list',
    category: 'Triage',
  },
  {
    id: 'allocate-board',
    label: 'Allocate the hovered / selected email to a Kanban board',
    displayKeys: ['a'],
    matchKey: 'a',
    scope: 'list',
    category: 'Organise',
  },
  // -- Organise ----------------------------------------------------------
  { id: 'toggle-pin', label: 'Pin / unpin (star) the email', displayKeys: ['p'], matchKey: 'p', scope: 'list', category: 'Organise' },
  { id: 'priority-normal', label: 'Set the linked card to Normal priority', displayKeys: ['1'], matchKey: '1', scope: 'list', category: 'Organise' },
  { id: 'priority-high', label: 'Set the linked card to High priority', displayKeys: ['2'], matchKey: '2', scope: 'list', category: 'Organise' },
  { id: 'priority-urgent', label: 'Set the linked card to Urgent priority', displayKeys: ['3'], matchKey: '3', scope: 'list', category: 'Organise' },
  // -- Reader (handlers in EmailPreviewPane / EmailReader) ----------------
  { id: 'reader-next', label: 'Next email (in the open email)', displayKeys: ['j'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-prev', label: 'Previous email (in the open email)', displayKeys: ['k'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-allocate-board', label: 'Allocate this email to a Kanban board', displayKeys: ['A'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-done', label: 'Done — archive and mark the linked card done', displayKeys: ['D'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-open-board', label: 'Open the board this email is allocated to', displayKeys: ['B'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-ping', label: 'Ping / assign a teammate', displayKeys: ['@'], scope: 'reader', category: 'In the open email' },
  { id: 'reader-markup', label: 'Paste a screenshot from the clipboard and mark it up', displayKeys: [MOD, 'Shift', 'M'], scope: 'reader', category: 'In the open email' },
  // -- Help ---------------------------------------------------------------
  { id: 'cheat-sheet', label: 'Show / hide keyboard shortcuts', displayKeys: ['?'], matchKey: '?', scope: 'list', category: 'Help' },
]

/** Priority keys → the Kanban card priority integer they set. */
export const EMAIL_PRIORITY_BY_ID: Record<string, { value: number; label: string }> = {
  'priority-normal': { value: 1, label: 'Normal' },
  'priority-high': { value: 2, label: 'High' },
  'priority-urgent': { value: 3, label: 'Urgent' },
}

/**
 * matchEmailShortcut({key, mod}) → the list-scope shortcut for a keydown, or
 * null. Pure I→O so the handler and tests share one resolution path.
 * `mod` = metaKey || ctrlKey on the event.
 */
export function matchEmailShortcut(input: {
  key: string
  mod?: boolean
}): EmailShortcutDef | null {
  const mod = !!input.mod
  // Single-char keys match case-insensitively (Shift+C is still Complete);
  // named keys (ArrowDown, Enter, Escape) match verbatim.
  const key = input.key.length === 1 ? input.key.toLowerCase() : input.key
  return (
    EMAIL_SHORTCUTS.find(
      (s) =>
        s.scope === 'list' &&
        s.matchKey !== undefined &&
        (s.matchKey.length === 1 ? s.matchKey.toLowerCase() : s.matchKey) === key &&
        !!s.mod === mod,
    ) ?? null
  )
}
